// Axios 请求封装
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
} from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import type { ApiResponse } from '@/types/api'

// 当前部署版本（用于检测版本变化）
let currentDeploymentVersion: string | null = null

/**
 * 检查并处理版本变化
 */
const checkVersionChange = (responseVersion: string) => {
  if (!currentDeploymentVersion) {
    currentDeploymentVersion = responseVersion
    localStorage.setItem('deploymentVersion', responseVersion)
    return false
  }

  if (currentDeploymentVersion !== responseVersion) {
    // 版本已变化，需要重新登录
    console.log('部署版本已变化:', currentDeploymentVersion, '->', responseVersion)
    return true
  }

  return false
}

/**
 * 清除认证信息并提示用户
 */
const handleVersionChange = () => {
  const authStore = getAuthStore()
  authStore.clearAuth()
  localStorage.removeItem('deploymentVersion')
  currentDeploymentVersion = null

  ElMessage.warning({
    message: '系统已更新，请重新登录',
    duration: 3000,
    onClose: () => {
      router.push('/login')
    }
  })
}

// 成功拦截器（见下方 response.interceptors.response.use）在 return 前已拆包：
//   return res.data  ← 实际返回的是「业务载荷 T」，而非 AxiosResponse<T>
// 因此 RequestMethods 把 get/post/put/delete 的返回类型重键为 Promise<T>，与运行时语义一致。
// 同时保留与 axios 相同的泛型形参顺序 <T, R, D>，避免调用处已写 `request.post<X, Y>(url, body)` 的
// 既有类型实参错位（历史上曾因把第二形参误当 data 类型引发雪崩）。
// 注意：Omit<AxiosInstance, ...> 会剥离 AxiosInstance 的可调用签名，故在 RequestMethods 中手动补齐。
interface RequestMethods {
  // 保留 AxiosInstance 的可调用签名（Omit 会剥离它，需在此重新声明）
  <T = any, R = T, D = any>(config: AxiosRequestConfig<D>): Promise<R>
  <T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>
  get<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>
  delete<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R>
  post<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>
  put<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R>
}

// 扩展 AxiosInstance：覆盖 get/post/put/delete 的返回类型为「已拆包的业务载荷」，
// 并在类型层补齐运行时挂载的自定义 download 方法（见 request.download 赋值），
// 否则调用方（如 api/mold.ts 的 request.download）报 TS2339。
type RequestInstance = Omit<AxiosInstance, 'get' | 'post' | 'put' | 'delete'> &
  RequestMethods & {
    download: (url: string, config?: AxiosRequestConfig) => Promise<void>
  }

// 创建 axios 实例
const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
}) as unknown as RequestInstance

// 是否正在刷新Token的标记
let isRefreshing = false
// 等待刷新完成的请求队列
let refreshSubscribers: Array<(token: string) => void> = []

/**
 * 检查Token是否过期或即将过期（提前5分钟刷新）
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: JwtPayload = jwtDecode(token)
    const exp = decoded.exp ? decoded.exp * 1000 : 0 // 转换为毫秒
    const now = Date.now()
    // 提前5分钟刷新（300000ms）
    return now >= exp - 300000
  } catch (error) {
    // Token解析失败，认为已过期
    return true
  }
}

/**
 * 添加请求到等待队列
 */
const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

/**
 * 通知所有等待的请求
 */
const notifyRefreshSubscribers = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token))
  refreshSubscribers = []
}

/**
 * 获取auth store
 */
const getAuthStore = () => {
  return useAuthStore()
}

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 当发送 FormData 时，删除默认的 Content-Type，让浏览器自动设置 multipart/form-data boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查响应头中的部署版本
    const responseVersion = response.headers['x-deployment-version']
    if (responseVersion && checkVersionChange(responseVersion)) {
      handleVersionChange()
      return Promise.reject(new Error('版本已更新'))
    }

    // 检查响应体中的部署版本（health接口）
    const responseData = response.data as any
    if (responseData?.data?.deploymentVersion) {
      const version = responseData.data.deploymentVersion
      if (checkVersionChange(version)) {
        handleVersionChange()
        return Promise.reject(new Error('版本已更新'))
      }
    }

    // 如果是blob类型（文件下载），直接返回响应数据
    if (response.config.responseType === 'blob') {
      return response.data
    }

    // 后端返回格式：{ code, message, data }
    const res = response.data as ApiResponse<unknown>

    // 如果 code 不是 200，抛出错误
    if (res.code !== 200) {
      const error = new Error(res.message || '请求失败')
      ;(error as Error & { response?: AxiosResponse }).response = response
      return Promise.reject(error)
    }

    // 返回实际数据
    return res.data
  },
  async (error: Error & { response?: AxiosResponse; config?: AxiosRequestConfig; request?: XMLHttpRequest }) => {
    const originalRequest = error.config

    // config 缺失（极少数场景）直接拒绝，避免后续空引用
    if (!originalRequest) {
      return Promise.reject(error)
    }

    // 处理401未授权 - Token过期或无效
    // 处理403禁止访问 - 也可能是Token过期导致的
    const isAuthError = error.response?.status === 401 || error.response?.status === 403

    if (isAuthError) {
      const authStore = getAuthStore()
      const currentToken = authStore.token || localStorage.getItem('token')

      // 如果是refreshToken请求失败，不再重试
      if (originalRequest.url?.includes('/auth/refreshToken')) {
        authStore.clearAuth()
        router.push('/login')
        return Promise.reject(error)
      }

      // 检查Token是否过期或即将过期
      if (currentToken && isTokenExpired(currentToken)) {
        // Token已过期，尝试刷新
        if (!authStore.canRefreshToken) {
          ElMessage.error('登录已过期，请重新登录')
          authStore.clearAuth()
          router.push('/login')
          return Promise.reject(error)
        }

        // 尝试刷新Token
        if (!isRefreshing) {
          isRefreshing = true

          try {
            const success = await authStore.refreshAccessToken()

            if (success) {
              const newToken = authStore.token!
              // 通知所有等待的请求
              notifyRefreshSubscribers(newToken)
              // 重试原请求
              originalRequest.headers!.Authorization = `Bearer ${newToken}`
              return request(originalRequest)
            } else {
              throw new Error('Token刷新失败')
            }
          } catch (refreshError) {
            authStore.clearAuth()
            router.push('/login')
            return Promise.reject(error)
          } finally {
            isRefreshing = false
          }
        } else {
          // 正在刷新中，将请求加入队列等待
          return new Promise((resolve, reject) => {
            addRefreshSubscriber((token: string) => {
              originalRequest.headers!.Authorization = `Bearer ${token}`
              resolve(request(originalRequest))
            })

            // 超时处理：如果刷新超时，也拒绝请求
            setTimeout(() => {
              reject(new Error('Token刷新超时'))
            }, 10000)
          })
        }
      }

      // Token未过期，但收到403/401，说明是真正的权限问题或Token无效
      if (error.response?.status === 403) {
        // 检查是否是版本变化导致的
        const responseVersion = error.response.headers['x-deployment-version']
        if (responseVersion && checkVersionChange(responseVersion)) {
          handleVersionChange()
          return Promise.reject(error)
        }

        // 真正的权限问题
        ElMessage.error(error.response.data?.message || '权限不足，无法执行此操作')
        return Promise.reject(error)
      }

      // 如果不是401（token无效），直接跳转登录
      if (!authStore.canRefreshToken) {
        ElMessage.error('登录已过期，请重新登录')
        authStore.clearAuth()
        router.push('/login')
        return Promise.reject(error)
      }
    }

    // 处理其他错误响应
    if (error.response) {
      const status = error.response.status
      switch (status) {
        case 401:
          // 已在上方统一处理
          break
        case 403:
          // 已在上方统一处理
          break
        case 404:
          ElMessage.error('请求的资源不存在')
          break
        case 500:
          ElMessage.error('服务器内部错误')
          break
        default:
          ElMessage.error(error.response.data?.message || '请求失败')
      }
    } else if (error.request) {
      ElMessage.error('网络连接失败，请检查网络')
    } else {
      ElMessage.error('请求配置错误')
    }
    return Promise.reject(error)
  }
)

// 文件下载方法
request.download = async (url: string, config?: AxiosRequestConfig): Promise<void> => {
  // 获取 token
  const token = localStorage.getItem('token')

  // 直接使用 axios 发送请求，避免被响应拦截器拦截
  const response = (await axios.get(url, {
    baseURL: '/api',
    ...config,
    responseType: 'blob',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })) as AxiosResponse<Blob>

  // 创建 blob
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  // 获取文件名
  const contentDisposition = response.headers['content-disposition']
  let fileName = 'export.xlsx'
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
    if (match) {
      fileName = decodeURIComponent(match[1].replace(/['"]/g, ''))
    }
  }

  // 下载文件
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(link.href)
}

export default request
